/**
 * Turns a sparse list of AttendanceDay records into a *dense* month: one entry
 * for every calendar day, each carrying a resolved status.
 *
 * The API only stores a record for days that had a punch, which a table can
 * happily skip but a month strip cannot — every day needs a cell. This is the
 * single place that decides what a given day *is*, so the strip, the summary
 * tiles and the table can never disagree with each other.
 *
 * Precedence, most specific first:
 *   Weekly Off > Holiday > On Leave > Upcoming > Missing Punch > Late > Present > Absent
 *
 * Rest days, holidays and approved leave are all *scheduled* — they are known
 * for days that haven't happened yet, so they resolve past the Upcoming cutoff
 * and a full month reads correctly from the 1st. Only the attendance-derived
 * statuses stop at today, because "Absent" is a fact about a day that has been
 * and gone, not a prediction.
 *
 * Weekly Off deliberately outranks Holiday: when a public holiday lands on a
 * rest day the day is still a rest day, and demoting it would make the weekly-off
 * count stop matching the number of rest days in the month. The holiday's name is
 * kept in the remark so neither fact is lost.
 */

export const STATUS = {
  PRESENT: { key: "PRESENT", code: "P", label: "Present", tone: "success" },
  LATE: { key: "LATE", code: "L", label: "Late", tone: "warning" },
  MISSING_PUNCH: { key: "MISSING_PUNCH", code: "MP", label: "Missing Punch", tone: "violet" },
  ABSENT: { key: "ABSENT", code: "A", label: "Absent", tone: "danger" },
  WEEKLY_OFF: { key: "WEEKLY_OFF", code: "WO", label: "Weekly Off", tone: "muted" },
  HOLIDAY: { key: "HOLIDAY", code: "H", label: "Holiday", tone: "muted" },
  // code is replaced per-day by the leave type's own code (CL/SL/LWP/…)
  ON_LEAVE: { key: "ON_LEAVE", code: "L", label: "On Leave", tone: "info" },
  UPCOMING: { key: "UPCOMING", code: "—", label: "Upcoming", tone: "muted" },
  // no shift assigned: which days were meant to be worked is unknown, so no
  // claim is made about the day at all
  UNKNOWN: { key: "UNKNOWN", code: "?", label: "Not evaluated", tone: "muted" },
  // rostered, but the month holds not one punch — far more likely that this
  // employee isn't on attendance tracking than that they missed every day
  NOT_TRACKED: { key: "NOT_TRACKED", code: "NT", label: "Not tracked", tone: "muted" },
};

const pad2 = (n) => String(n).padStart(2, "0");

// Dates arrive as ISO timestamps; key everything on the UTC calendar day so a
// browser behind UTC can't shift a leave onto the previous day.
const utcDayString = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
};

/**
 * Holiday records span a range via startDate/endDate (endDate optional for
 * single-day holidays). Flatten to a dayString -> name map.
 */
export const indexHolidays = (holidays = []) => {
  const map = new Map();
  (holidays || []).forEach((h) => {
    if (!h?.startDate) return;
    const start = new Date(h.startDate);
    const end = h.endDate ? new Date(h.endDate) : start;
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return;
    for (let t = new Date(start); t <= end; t.setUTCDate(t.getUTCDate() + 1)) {
      map.set(utcDayString(t), h.name || "Holiday");
    }
  });
  return map;
};

/**
 * Flatten /leave-request/calendar entries to a dayString -> leave map. Only
 * approved leave marks a day; pending requests must not erase an absence.
 */
export const indexLeave = (entries = []) => {
  const map = new Map();
  (entries || []).forEach((entry) => {
    if (entry?.status !== "Approved") return;
    (entry.days || []).forEach((d) => {
      const key = utcDayString(d.date);
      if (!key) return;
      map.set(key, {
        code: entry.leaveCode || "L",
        name: entry.leaveType || "Leave",
        window: d.window || "full-day",
        isPaid: entry.isPaid,
      });
    });
  });
  return map;
};

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const pad = (n) => String(n).padStart(2, "0");

export const toDayString = (year, month, day) => `${year}-${pad(month + 1)}-${pad(day)}`;

export const daysInMonth = (year, month) => new Date(Date.UTC(year, month + 1, 0)).getUTCDate();

/**
 * @param year   full year, e.g. 2026
 * @param month  0-indexed, matching AttendanceDay.month and Date#getMonth
 * @param records AttendanceDay[] for (at least) this month
 * @param shift  resolved Shift — `days[weekday].isHoliday` marks weekly offs
 * @param holidays Holiday[] (any range; only this month's are used)
 * @param leave  /leave-request/calendar entries for this employee
 * @param today  Date used to decide which days are still upcoming
 */
export const buildAttendanceMonth = ({
  year,
  month,
  records = [],
  shift,
  holidays = [],
  leave = [],
  today = new Date(),
}) => {
  const byDayString = new Map();
  (records || []).forEach((r) => {
    if (r?.dayString) byDayString.set(r.dayString, r);
  });
  const holidayByDay = indexHolidays(holidays);
  const leaveByDay = indexLeave(leave);
  // a shift is what defines which weekdays are working days
  const shiftKnown = Array.isArray(shift?.days) && shift.days.length > 0;
  // One punch anywhere in the month proves the employee is on attendance
  // tracking, which makes the *other* blank days real absences. Zero punches
  // proves nothing, so the month is reported as untracked instead.
  const hasAnyRecord = byDayString.size > 0;

  // compare on the same UTC footing the day cells are built on
  const todayString = toDayString(today.getFullYear(), today.getMonth(), today.getDate());
  const total = daysInMonth(year, month);
  const days = [];

  for (let d = 1; d <= total; d += 1) {
    const dayString = toDayString(year, month, d);
    const weekday = new Date(Date.UTC(year, month, d)).getUTCDay();
    const record = byDayString.get(dayString) || null;
    const isWeeklyOff = Boolean(shift?.days?.[weekday]?.isHoliday);
    const isFuture = dayString > todayString;
    const holidayName = holidayByDay.get(dayString) || null;
    const leaveOnDay = leaveByDay.get(dayString) || null;

    let status;
    let remark = "";
    let code = null; // set only when the day's code differs from its status code

    if (isWeeklyOff) {
      status = STATUS.WEEKLY_OFF;
      // keep the coinciding holiday visible even though the rest day wins
      if (holidayName) remark = holidayName;
      // a punch on a rest day is worth surfacing rather than hiding
      else if (record?.arrivalTime != null) remark = "Worked on week off";
    } else if (holidayName) {
      status = STATUS.HOLIDAY;
      remark = holidayName;
      if (record?.arrivalTime != null) remark = `${holidayName} · worked`;
    } else if (leaveOnDay) {
      status = STATUS.ON_LEAVE;
      code = leaveOnDay.code;
      remark =
        leaveOnDay.window === "full-day"
          ? leaveOnDay.name
          : `${leaveOnDay.name} · ${leaveOnDay.window.replace("-", " ")}`;
    } else if (isFuture) {
      status = STATUS.UPCOMING;
    } else if (record?.arrivalTime == null) {
      // "Absent" means "was rostered and did not come" — it needs both a roster
      // and evidence the employee is being tracked at all.
      if (!shiftKnown) status = STATUS.UNKNOWN;
      else if (!hasAnyRecord) status = STATUS.NOT_TRACKED;
      else status = STATUS.ABSENT;
    } else if (record.departureTime == null || record.missedCheckout) {
      status = STATUS.MISSING_PUNCH;
      remark = record.isAutoClosed ? "Auto-closed at shift end" : "No check-out punch";
    } else if (record.hasArrivedLate) {
      status = STATUS.LATE;
      if (record.hasLeftEarly) remark = "Left early";
    } else {
      status = STATUS.PRESENT;
      if (record.hasLeftEarly) remark = "Left early";
    }

    // A corrected day is present on the same terms as any other — it is not a
    // lesser status, and downgrading it would misreport someone's attendance.
    // But the punch was asserted, not captured, so the day is marked: same
    // letter, an asterisk beside it. Only where a punch was actually involved;
    // an asterisk on a weekly off or a holiday would be noise.
    const corrected = Boolean(record?.isRegularized) && !isWeeklyOff && !holidayName && !leaveOnDay;

    days.push({
      dayString,
      dayNum: d,
      weekday: WEEKDAYS[weekday],
      status,
      code: code || status.code,
      corrected,
      remark,
      record,
      leave: leaveOnDay,
      holidayName,
      isFuture,
      isWeeklyOff,
      // positive minutes past shift start, only meaningful when late
      lateBy: record?.arrivalTimeDifference > 0 ? record.arrivalTimeDifference : null,
      arrivalTime: record?.arrivalTime ?? null,
      departureTime: record?.departureTime ?? null,
      totalHours: record?.totalHours || 0,
      effectiveHours: record?.effectiveHours || 0,
    });
  }

  const count = (key) => days.filter((x) => x.status.key === key).length;
  const summary = {
    present: count("PRESENT"),
    late: count("LATE"),
    absent: count("ABSENT"),
    missingPunch: count("MISSING_PUNCH"),
    weeklyOff: count("WEEKLY_OFF"),
    holiday: count("HOLIDAY"),
    onLeave: count("ON_LEAVE"),
    unknown: count("UNKNOWN"),
    notTracked: count("NOT_TRACKED"),
    shiftKnown,
    hasAnyRecord,
    // minutes actually worked, from every day that has a record — including
    // time punched on a rest day or during leave, which really was worked
    totalHours: days.reduce((sum, x) => sum + (x.totalHours || 0), 0),
  };

  return { days, summary };
};
